import { useMutation } from '@apollo/client';
import { gql } from '__generated__/gql';
import { ShareAssetStatusEnumType } from '__generated__/graphql';
import { FC, useRef } from 'react';

import Button from 'primitives/Button';
import { Dialog, DialogActions, DialogPanel, DialogSubTitle, DialogTitle } from 'primitives/Dialog';

import { FormInput, FormPanel } from 'components/FormPanel';

import { investorRightOptions } from './investorRightsOptions';

const UPDATE_SHARE_ASSET_MUTATION = gql(`
  mutation UpdateShareAssetDialog(
    $id: ID!
    $issueNumberOfShares: Float!
    $numberOfShares: Float!
    $status: ShareAssetStatusEnumType!
    $investorRights: [InvestorRightEnumType]
    $documents: [Upload]
  ) {
    updateShareAsset(
      id: $id
      issueNumberOfShares: $issueNumberOfShares
      numberOfShares: $numberOfShares
      status: $status
      investorRights: $investorRights
      documents: $documents
    ) {
      id
      issueNumberOfShares
      numberOfShares
      status
      updatedAt
      investorRights
      documents {
        id
        url
        name
      }
    }
  }
`);

const UpdateShareAssetDialog: FC<{
  shareAsset: {
    id: string;
    issueNumberOfShares: number;
    numberOfShares: number;
    status: ShareAssetStatusEnumType;
    investorRights?: string[];
    documents?: {
      id: string;
      url: string;
      name: string;
    }[];
  };
  onClose: () => void;
}> = ({ shareAsset, onClose }) => {
  const buttonRef = useRef<HTMLButtonElement>(null);

  const [updateShareAsset, { loading, error }] = useMutation(UPDATE_SHARE_ASSET_MUTATION);

  return (
    <>
      <Dialog open={true} onClose={onClose}>
        <DialogPanel>
          <DialogTitle>Update Share Asset</DialogTitle>
          <DialogSubTitle>Make changes to the share asset</DialogSubTitle>
          <FormPanel
            loading={loading}
            error={error}
            onCancel={onClose}
            onSubmit={data =>
              updateShareAsset({
                variables: {
                  id: shareAsset.id,
                  issueNumberOfShares: data.issueNumberOfShares,
                  numberOfShares: data.numberOfShares,
                  status: data.status,
                  investorRights: data.investorRights,
                  documents: data.documents,
                },
              }).then(onClose)
            }
            buttonRef={buttonRef}
          >
            <FormInput
              type="number"
              fieldName="issueNumberOfShares"
              label="Issue Number of Shares"
              defaultValue={shareAsset.issueNumberOfShares}
              validators={{ required: true }}
            />
            <FormInput
              type="number"
              fieldName="numberOfShares"
              label="Number of Shares"
              defaultValue={shareAsset.numberOfShares}
              validators={{ required: true }}
            />
            <FormInput
              type="select"
              fieldName="status"
              label="Status"
              defaultValue={shareAsset.status}
              validators={{ required: true }}
              options={[
                { value: ShareAssetStatusEnumType.Active, label: 'Active' },
                { value: ShareAssetStatusEnumType.Inactive, label: 'Inactive' },
                { value: ShareAssetStatusEnumType.Exited, label: 'Exited' },
                { value: ShareAssetStatusEnumType.UnderReview, label: 'Under Review' },
              ]}
            />
            <FormInput
              type="multi_select"
              fieldName="investorRights"
              label="Investor Rights"
              defaultValue={shareAsset.investorRights}
              options={investorRightOptions}
              placeholder="Select investor rights..."
            />
            <FormInput
              type="upload"
              fieldName="documents"
              label="Documents"
              defaultValue={shareAsset.documents}
            />
          </FormPanel>
          <DialogActions>
            <Button
              variant="secondary"
              onClick={() => {
                // @ts-ignore
                buttonRef.current?.cancel();
              }}
            >
              Cancel
            </Button>
            <Button
              loading={loading}
              onClick={() => {
                // @ts-ignore
                buttonRef.current?.submit();
              }}
            >
              Submit
            </Button>
          </DialogActions>
        </DialogPanel>
      </Dialog>
    </>
  );
};

export default UpdateShareAssetDialog;
